/**
 * A tela 2 do Contrato Digital, "Contratos digitais" (E177.F4, GUS-253, ADR-0130; HTML 6.8 por CCB):
 * o processo e a pasta (só leitura), quantas CCBs o envio tem, um bloco por CCB com os arquivos
 * dela, o aviso de como o sistema trata e o "Ver recibo". A mesma no escritório, no banco e na
 * plataforma.
 *
 * - **CCBs neste envio** acrescenta blocos sem número; diminuir só tira os blocos sem número do fim
 *   (a CCB gravada sai pelo "Remover esta CCB", sem arquivo).
 * - **Conferir** só com todas as CCBs numeradas, cada uma com arquivo de origem recebido, nada em
 *   envio nem com falha, nenhum número em edição (E177.F4.C01) e nenhum arquivo sem CCB
 *   (`podeConferir`).
 * - A tela relê a abertura a cada arquivo que entra e enquanto o material é lido. A releitura que
 *   falha segura o "Conferir" e oferece ler de novo (E135.F2.C03): a tela não sabe o que mudou.
 * - O envio sem CCB que o recibo mostra sem conclusão (anterior às CCBs) e o guardado sem exame dele
 *   ficam acima dos blocos, como os arquivos sem CCB.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  LinhaNaoExaminado,
  ReciboNaoLido,
  ReciboNaoSelado,
} from '@/components/CaseWizard/LinhasDoEnvio';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { AberturaDoCasoData } from '@/lib/abertura_do_caso';
import { jaRemovido, removerNaoExaminado } from '@/lib/recebimentos';
import { useEnvioPorRecebimento } from '@/lib/useEnvioPorRecebimento';

import { BlocoDaCcbNaTela } from './BlocoDaCcb';
import {
  blocosDoEnvio,
  doReciboDaCcb,
  estadoDaTela,
  podeConferir,
  semCcb,
  type DoReciboDaCcb,
} from './blocosDasCcbs';
import {
  AvisoDaAbertura,
  BOTAO_DO_RODAPE,
  CartaoDaAbertura,
  RODAPE_DA_JORNADA,
} from './PecasDaAbertura';
import { ReciboNoEnvio } from './ReciboNoEnvio';
import { useAberturaDoCaso } from './useAberturaDoCaso';
import { ArquivosSemCcb, VinculosDoArquivo, VincularJaEnviado } from './VinculosDaCcb';

const t = ptBR.abertura.contratosDigitais;

export interface ContratosDigitaisProps {
  client: AxiosInstance;
  caseId: string;
  onVoltar: () => void;
  onConferir: () => void;
  onVerRecibo: () => void;
}

let proximo = 0;
function chaveLocal(): string {
  proximo += 1;
  return `sem-numero-${proximo}`;
}

/**
 * Os blocos sem número da tela; nenhum guardado ainda, um só quando o envio não tem CCB.
 *
 * E177.F4.C03 (GUS-253 item 2): toda mudança parte da lista ATUAL. A gravação do número termina
 * depois do "Adicionar outra CCB" clicado durante ela; tirar o bloco gravado da lista que o clique
 * viu apagava o bloco novo.
 */
function useBlocosSemNumero(dados: AberturaDoCasoData | null) {
  const [guardados, setGuardados] = useState<string[] | null>(null);
  const temCcb = (dados?.envio?.contratos ?? []).some((c) => c.ccb != null);
  const [inicial] = useState(chaveLocal);
  const semNumero = guardados ?? (temCcb || dados === null ? [] : [inicial]);
  const atual = useRef(semNumero);
  atual.current = semNumero;
  const mudarSemNumero = useCallback(
    (mudar: (lista: readonly string[]) => string[]) =>
      setGuardados((antes) => mudar(antes ?? atual.current)),
    [],
  );
  return { semNumero, mudarSemNumero };
}

function Cabecalho({
  dados,
  quantas,
  onQuantas,
  aviso,
}: {
  dados: AberturaDoCasoData;
  quantas: number;
  onQuantas: (n: number) => void;
  aviso: string | null;
}): JSX.Element {
  // O que a pessoa digita: apagar o número não volta a contagem antes de ela digitar o novo.
  const [rascunho, setRascunho] = useState<string | null>(null);
  return (
    <CartaoDaAbertura titulo={t.cardTitulo} testId="ccbs-cabecalho">
      <div className="grid gap-sc-4 md:grid-cols-3">
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-fg">
          {t.processo}
          <input
            readOnly
            aria-readonly="true"
            value={dados.process_number ?? t.naoInformado}
            className="h-9 border border-border bg-surface-2 px-3 text-[14px] font-normal"
            data-testid="ccbs-processo"
          />
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-fg">
          {t.pasta}
          <input
            readOnly
            aria-readonly="true"
            value={dados.pasta ?? t.naoInformada}
            className="h-9 border border-border bg-surface-2 px-3 text-[14px] font-normal"
            data-testid="ccbs-pasta"
          />
        </label>
        <label className="flex flex-col gap-1 text-[13px] font-semibold text-fg">
          {t.quantas}
          <input
            type="number"
            min={1}
            max={20}
            value={rascunho ?? String(quantas)}
            onChange={(evt) => {
              const valor = evt.target.value;
              setRascunho(valor);
              const n = Number(valor);
              if (valor !== '' && Number.isInteger(n) && n >= 1) onQuantas(n);
            }}
            onBlur={() => setRascunho(null)}
            className="h-9 border border-border-strong bg-bg px-3 text-[14px] font-normal"
            data-testid="ccbs-quantas"
          />
        </label>
      </div>
      <p className="mt-sc-3 text-[13px] text-fg-muted">{t.quantasAjuda}</p>
      {aviso !== null ? (
        <p role="status" className="mt-sc-2 text-[13px] text-fg" data-testid="ccbs-quantas-aviso">
          {aviso}
        </p>
      ) : null}
    </CartaoDaAbertura>
  );
}

const SEM_CCB = 'envio-sem-ccb';

/** O envio sem CCB do recibo: o sem conclusão, com o "Concluir o envio", e o guardado sem exame. */
function DoEnvioSemCcb({
  client,
  caseId,
  doRecibo,
  onRecarregar,
  onPendencias,
}: {
  client: AxiosInstance;
  caseId: string;
  doRecibo: DoReciboDaCcb;
  onRecarregar: () => Promise<void>;
  onPendencias: (chave: string, pendencias: number, falhas: number) => void;
}): JSX.Element | null {
  const envio = useEnvioPorRecebimento({
    client,
    caseId,
    kind: 'contested',
    abertosNoRecibo: doRecibo.abertos,
    onEntrada: () => undefined,
    onConcluido: () => void onRecarregar(),
  });
  const falhas = envio.recibosNaoSelados.length;
  useEffect(
    () => onPendencias(SEM_CCB, envio.pendencias, falhas),
    [envio.pendencias, falhas, onPendencias],
  );
  if (envio.recibosNaoSelados.length === 0 && doRecibo.naoExaminados.length === 0) return null;
  return (
    <ul className="my-3 flex flex-col gap-2" data-testid="ccbs-envio-sem-ccb">
      {envio.recibosNaoSelados.map((id) => (
        <ReciboNaoSelado
          key={id}
          recebimentoId={id}
          onConcluirDeNovo={() => void envio.concluirDeNovo(id)}
        />
      ))}
      {doRecibo.naoExaminados.map((item) => (
        <LinhaNaoExaminado
          key={item.id}
          item={item}
          onRemover={() =>
            void removerNaoExaminado(client, caseId, item.id).then(onRecarregar, (err: unknown) =>
              jaRemovido(err) ? onRecarregar() : undefined,
            )
          }
        />
      ))}
    </ul>
  );
}

export function ContratosDigitais(props: ContratosDigitaisProps): JSX.Element {
  const { client, caseId } = props;
  const { dados, erro, recarregar } = useAberturaDoCaso(client, caseId, { releitura: true });
  const { semNumero, mudarSemNumero } = useBlocosSemNumero(dados);
  // As pendências e as falhas de cada bloco (e do envio sem CCB), pela chave.
  const [pendencias, setPendencias] = useState<Record<string, readonly [number, number]>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  const [numerosEmEdicao, setNumerosEmEdicao] = useState<Record<string, boolean>>({});
  // E209.F3.T02 (GUS-296 item 12a): o que se soltou no bloco antes da declaração, à espera do bloco
  // da CCB declarada (o bloco troca de chave e a tela o monta de novo).
  const [aEnviarPorCcb, setAEnviarPorCcb] = useState<Record<string, readonly File[]>>({});
  const aoEnviados = useCallback(
    (chave: string) =>
      setAEnviarPorCcb((atual) =>
        chave in atual
          ? Object.fromEntries(Object.entries(atual).filter(([outra]) => outra !== chave))
          : atual,
      ),
    [],
  );
  const aoNumeroPendente = useCallback(
    (chave: string, pendente: boolean) =>
      setNumerosEmEdicao((atual) =>
        atual[chave] === pendente ? atual : { ...atual, [chave]: pendente },
      ),
    [],
  );
  const aoPendencias = useCallback(
    (chave: string, n: number, falhas: number) =>
      setPendencias((atual) => {
        const antes = atual[chave];
        return antes?.[0] === n && antes[1] === falhas ? atual : { ...atual, [chave]: [n, falhas] };
      }),
    [],
  );
  if (dados === null) {
    return erro ? (
      <p role="alert" className="text-body-sm text-danger" data-testid="ccbs-erro">
        {t.erroAoCarregar}
      </p>
    ) : (
      <p className="text-body-sm text-fg-muted">{t.carregando}</p>
    );
  }
  const blocos = blocosDoEnvio(dados.envio, semNumero);
  const soltos = semCcb(dados.envio);
  const contadas = [...blocos.map((b) => b.chave), SEM_CCB].map((k) => pendencias[k] ?? [0, 0]);
  const estado = {
    blocos,
    pendencias: contadas.reduce((n, [p]) => n + p, 0),
    falhas: contadas.reduce((n, [, f]) => n + f, 0),
    numerosPendentes: blocos.filter((b) => numerosEmEdicao[b.chave] === true).length,
    semCcb: soltos.length,
  };

  function tirarDaTela(chave: string): void {
    mudarSemNumero((lista) => lista.filter((k) => k !== chave));
  }

  function aoDeclarada(chave: string, ccbId: string, aEnviar: readonly File[]): void {
    if (aEnviar.length > 0) setAEnviarPorCcb((atual) => ({ ...atual, [ccbId]: aEnviar }));
    tirarDaTela(chave);
  }

  function mudarQuantas(n: number): void {
    const alvo = Math.max(1, Math.min(20, n));
    const fixos = blocos.length - semNumero.length;
    setAviso(alvo < fixos ? t.quantasMinimo : null);
    const quer = Math.max(alvo - fixos, fixos === 0 ? 1 : 0);
    mudarSemNumero((lista) => {
      const novos = lista.slice(0, quer);
      while (novos.length < quer) novos.push(chaveLocal());
      return novos;
    });
  }

  return (
    <div className="flex flex-col gap-5" data-testid="ccbs-tela">
      <Cabecalho dados={dados} quantas={blocos.length} onQuantas={mudarQuantas} aviso={aviso} />
      <div>
        <p role="status" className="text-[14px] text-fg" data-testid="ccbs-estado">
          {estadoDaTela(estado)}
        </p>
        {erro ? <ReciboNaoLido onLerDeNovo={() => void recarregar()} /> : null}
        <DoEnvioSemCcb
          client={client}
          caseId={caseId}
          doRecibo={doReciboDaCcb(dados.recibo, null)}
          onRecarregar={recarregar}
          onPendencias={aoPendencias}
        />
        <ArquivosSemCcb
          client={client}
          caseId={caseId}
          soltos={soltos}
          blocos={blocos}
          onRecarregar={recarregar}
        />
        {blocos.map((bloco, indice) => (
          <BlocoDaCcbNaTela
            key={bloco.chave}
            client={client}
            caseId={caseId}
            bloco={bloco}
            indice={indice}
            blocos={blocos}
            onRecarregar={recarregar}
            onDeclarada={aoDeclarada}
            aEnviar={aEnviarPorCcb[bloco.chave]}
            onEnviados={aoEnviados}
            onTirarDaTela={tirarDaTela}
            onPendencias={aoPendencias}
            onNumeroPendente={aoNumeroPendente}
            doRecibo={bloco.ccb !== null ? doReciboDaCcb(dados.recibo, bloco.ccb.id) : undefined}
            vinculos={(documentoId) => (
              <VinculosDoArquivo
                client={client}
                caseId={caseId}
                bloco={bloco}
                blocos={blocos}
                documentoId={documentoId}
                onRecarregar={recarregar}
              />
            )}
            depoisDosArquivos={
              <VincularJaEnviado
                client={client}
                caseId={caseId}
                bloco={bloco}
                blocos={blocos}
                onRecarregar={recarregar}
              />
            }
          />
        ))}
        <Button
          type="button"
          variant="secondary"
          onClick={() => mudarSemNumero((lista) => [...lista, chaveLocal()])}
          data-testid="ccbs-adicionar"
          disabled={blocos.length >= 20}
        >
          {t.adicionar}
        </Button>
      </div>
      <AvisoDaAbertura
        tom="info"
        titulo={t.comoTrataTitulo}
        texto={t.comoTrataTexto}
        testId="ccbs-como-trata"
      />
      <ReciboNoEnvio onVerRecibo={props.onVerRecibo} />
      <div className={RODAPE_DA_JORNADA}>
        <Button
          type="button"
          variant="secondary"
          className={BOTAO_DO_RODAPE}
          onClick={props.onVoltar}
        >
          {t.voltar}
        </Button>
        <Button
          type="button"
          className={BOTAO_DO_RODAPE}
          onClick={props.onConferir}
          disabled={erro || !podeConferir(estado)}
          data-testid="ccbs-conferir"
        >
          {ptBR.abertura.conferirAcao}
        </Button>
      </div>
    </div>
  );
}
